import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import { readingsApi } from '../api/readings'
import { scadaApi, type PumpStatus, type ReservoirStatus, type ZoneFlowStatus } from '../api/scada'
import { useAuthStore } from '../store/authStore'
import { Badge, severityTone } from '../components/Badge'

// ── Meter-reading data hook ──────────────────────────────────────────────────

function useAnalyticsData(enabled: boolean) {
  const { data: meters = [], isLoading: metersLoading } = useQuery({
    queryKey: ['meters', '', false, 0],
    queryFn: () => readingsApi.getMeters({ active: true, limit: 500 }).then((r) => r.data),
    staleTime: 60_000,
    enabled,
  })

  const { data: readings = [], isLoading: readingsLoading } = useQuery({
    queryKey: ['readings-all'],
    queryFn: () => readingsApi.list({ limit: 500 }).then((r) => r.data),
    staleTime: 60_000,
    enabled,
  })

  // Monthly trend — group readings by YYYY-MM
  const byMonth: Record<string, number[]> = {}
  readings.forEach((r) => {
    const month = r.reading_date.slice(0, 7)
    if (!byMonth[month]) byMonth[month] = []
    byMonth[month].push(r.reading_value)
  })

  const monthlyTrend = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-8)
    .map(([month, vals], i, arr) => {
      const total = Math.round(vals.reduce((s, v) => s + v, 0))
      const prevTotal = i > 0 ? Math.round(arr[i - 1][1].reduce((s: number, v) => s + v, 0)) : null
      return {
        month: new Date(month + '-01').toLocaleString('sr-Latn-RS', { month: 'short', year: '2-digit' }),
        total,
        prev: prevTotal,
        count: vals.length,
      }
    })

  // Top consumers — meters with highest last reading value
  const topMeters = [...meters]
    .filter((m) => m.last_reading_value !== null)
    .sort((a, b) => (b.last_reading_value ?? 0) - (a.last_reading_value ?? 0))
    .slice(0, 10)

  // Coverage stats
  const totalMeters  = meters.length
  const readMeters   = meters.filter((m) => m.last_reading_value !== null).length
  const coverage     = totalMeters > 0 ? Math.round((readMeters / totalMeters) * 100) : 0

  // Readings per month chart
  const readingsPerMonth = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([month, vals]) => ({
      month: new Date(month + '-01').toLocaleString('sr-Latn-RS', { month: 'short' }),
      count: vals.length,
    }))

  return {
    monthlyTrend,
    topMeters,
    totalMeters,
    readMeters,
    coverage,
    readingsPerMonth,
    isLoading: metersLoading || readingsLoading,
  }
}

// ── SCADA analytics data hook ────────────────────────────────────────────────

const ZONE_STATUS_LABEL: Record<ZoneFlowStatus, string> = {
  deficit: 'Deficit', surplus: 'Suficit', balanced: 'Balansirano', unknown: 'Nepoznato',
}
const HISTORY_HOURS_OPTIONS = [6, 24, 72] as const

function useScadaAnalyticsData() {
  const { data: latest, isLoading: latestLoading } = useQuery({
    queryKey: ['scada-latest-for-analytics'],
    queryFn: () => scadaApi.latest().then((r) => r.data),
    staleTime: 30_000,
  })

  const { data: zoneStatuses = [], isLoading: zoneLoading } = useQuery({
    queryKey: ['scada-zone-status-for-analytics'],
    queryFn: () => scadaApi.zoneStatus().then((r) => r.data),
    staleTime: 30_000,
  })

  const pumps      = latest?.pumps ?? []
  const reservoirs = latest?.reservoirs ?? []
  const totalDevices  = pumps.length + reservoirs.length
  const offlineCount  =
    pumps.filter((p) => p.status === 'no_signal').length +
    reservoirs.filter((r) => r.status === 'no_signal').length
  const onlineCount = totalDevices - offlineCount

  // Water-loss per zone — nominal_flow_lps/net_flow_lps are already computed
  // server-side (services/alerts.py's zone_flow_statuses) for the topology
  // view's status dot, but the actual numbers were never surfaced anywhere.
  const waterLoss = zoneStatuses
    .filter((z) => z.nominal_flow_lps !== null && z.net_flow_lps !== null)
    .map((z) => {
      const loss = z.nominal_flow_lps! - z.net_flow_lps!
      const lossPct = z.nominal_flow_lps ? (loss / z.nominal_flow_lps) * 100 : null
      return { ...z, loss, lossPct }
    })
    .sort((a, b) => (b.lossPct ?? -Infinity) - (a.lossPct ?? -Infinity))

  return {
    pumps, reservoirs, totalDevices, onlineCount, offlineCount,
    zonesTracked: zoneStatuses.length, waterLoss,
    isLoading: latestLoading || zoneLoading,
  }
}

function useDeviceHistory(deviceKey: string | null, nodeType: 'pump' | 'reservoir' | null, hours: number) {
  return useQuery({
    queryKey: ['scada-analytics-device-history', deviceKey, nodeType, hours],
    queryFn: async () => {
      if (!deviceKey || !nodeType) return []
      return nodeType === 'pump'
        ? (await scadaApi.pumpHistory(deviceKey, hours)).data
        : (await scadaApi.reservoirHistory(deviceKey, hours)).data
    },
    enabled: deviceKey !== null && nodeType !== null,
  })
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-5">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1 leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

// ── SCADA analytics section ──────────────────────────────────────────────────

function ScadaAnalyticsSection() {
  const {
    pumps, reservoirs, totalDevices, onlineCount, offlineCount,
    zonesTracked, waterLoss, isLoading,
  } = useScadaAnalyticsData()

  const devices: { key: string; label: string; nodeType: 'pump' | 'reservoir' }[] = [
    ...pumps.map((p: PumpStatus) => ({ key: p.device_key, label: p.label, nodeType: 'pump' as const })),
    ...reservoirs.map((r: ReservoirStatus) => ({ key: r.device_key, label: r.label, nodeType: 'reservoir' as const })),
  ]

  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [hours, setHours] = useState<typeof HISTORY_HOURS_OPTIONS[number]>(24)
  const selected = devices.find((d) => d.key === selectedKey) ?? devices[0] ?? null

  const { data: history = [], isLoading: historyLoading } = useDeviceHistory(
    selected?.key ?? null, selected?.nodeType ?? null, hours,
  )

  const historyChartData = history.map((h) => ({
    t: new Date(h.recorded_at).toLocaleString('sr-Latn-RS', {
      day: hours > 24 ? '2-digit' : undefined, month: hours > 24 ? '2-digit' : undefined,
      hour: '2-digit', minute: '2-digit',
    }),
    v: selected?.nodeType === 'pump' ? (h as { flow_lps: number | null }).flow_lps : (h as { depth_m: number }).depth_m,
  }))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="SCADA uređaji" value={totalDevices} sub="pumpe i rezervoari" />
        <StatCard label="Trenutno online" value={onlineCount} />
        <StatCard label="Trenutno offline" value={offlineCount} sub={offlineCount > 0 ? 'proverite vezu' : undefined} />
        <StatCard label="Praćene zone" value={zonesTracked} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Water-loss table */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Gubici po zonama</h2>
            <p className="text-xs text-gray-500 mt-0.5">Nominalni protok naspram stvarnog, poslednji sat</p>
          </div>
          {waterLoss.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-400 text-sm">
              Nema zona sa definisanim nominalnim protokom
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Zona</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nominalno (l/s)</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stvarno (l/s)</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Gubitak</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {waterLoss.map((z) => (
                    <tr key={z.zone_id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900">{z.label}</td>
                      <td className="px-6 py-3 text-right text-gray-600">{z.nominal_flow_lps!.toFixed(1)}</td>
                      <td className="px-6 py-3 text-right text-gray-600">{z.net_flow_lps!.toFixed(1)}</td>
                      <td className="px-6 py-3 text-right font-medium text-gray-900">
                        {z.loss.toFixed(1)} {z.lossPct !== null && <span className="text-gray-400">({z.lossPct.toFixed(0)}%)</span>}
                      </td>
                      <td className="px-6 py-3">
                        <Badge tone={severityTone(z.severity)}>{ZONE_STATUS_LABEL[z.status]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Device trend chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6">
          <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
            <h2 className="text-base font-semibold text-gray-900">Trend uređaja</h2>
            <div className="flex gap-1">
              {HISTORY_HOURS_OPTIONS.map((h) => (
                <button key={h} onClick={() => setHours(h)}
                  className={`px-2 py-0.5 text-xs font-medium rounded-md border transition-colors ${
                    hours === h ? 'bg-brand-cyan text-brand-midnight border-brand-cyan' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {h}h
                </button>
              ))}
            </div>
          </div>
          {devices.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Nema uređaja</div>
          ) : (
            <>
              <select
                value={selected?.key ?? ''}
                onChange={(e) => setSelectedKey(e.target.value)}
                className="w-full mb-3 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                {devices.map((d) => (
                  <option key={d.key} value={d.key}>{d.label} ({d.nodeType === 'pump' ? 'pumpa' : 'rezervoar'})</option>
                ))}
              </select>
              {historyLoading ? (
                <div className="h-[190px] flex items-center justify-center text-gray-400 text-sm">Učitavanje…</div>
              ) : historyChartData.length === 0 ? (
                <div className="h-[190px] flex items-center justify-center text-gray-400 text-sm">Nema podataka za izabrani period</div>
              ) : (
                <ResponsiveContainer width="100%" height={190}>
                  <LineChart data={historyChartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="v" name={selected?.nodeType === 'pump' ? 'Protok (l/s)' : 'Nivo (m)'}
                      stroke="var(--color-brand-cyan)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AnalystView() {
  const { user } = useAuthStore()
  const hasMeterReading = user?.modules?.includes('meter_reading') ?? false
  const hasScada        = user?.modules?.includes('scada') ?? false

  const {
    monthlyTrend, topMeters, totalMeters,
    readMeters, coverage, readingsPerMonth, isLoading,
  } = useAnalyticsData(hasMeterReading)

  if (hasMeterReading && isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {hasScada && (
        <section>
          {hasMeterReading && <h2 className="text-lg font-bold text-gray-900 mb-4">SCADA</h2>}
          <ScadaAnalyticsSection />
        </section>
      )}

      {hasMeterReading && (
        <section className="space-y-6">
          {hasScada && <h2 className="text-lg font-bold text-gray-900">Vodomeri</h2>}
          {/* KPI row */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Ukupno vodomera" value={totalMeters} />
            <StatCard label="Ima očitavanje" value={readMeters} sub="aktivnih vodomera" />
            <StatCard
              label="Pokrivenost"
              value={`${coverage}%`}
              sub={`${totalMeters - readMeters} bez očitavanja`}
            />
            <StatCard
              label="Ukupno očitavanja"
              value={readingsPerMonth.reduce((s, m) => s + m.count, 0)}
              sub="poslednjih 6 meseci"
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Monthly trend chart */}
            <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 shadow-xs p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Mesečni zbir očitavanja (m³)
              </h2>
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyTrend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => [`${v.toLocaleString()} m³`]} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Ukupno m³"
                      stroke="var(--color-brand-cyan)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
                  Nema dovoljno podataka
                </div>
              )}
            </div>

            {/* Readings per month bar */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Broj očitavanja / mesec
              </h2>
              {readingsPerMonth.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={readingsPerMonth} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Očitavanja" fill="var(--color-brand-cyan)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
                  Nema podataka
                </div>
              )}
            </div>
          </div>

          {/* Top consumers table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Najveći potrošači</h2>
              <p className="text-xs text-gray-500 mt-0.5">Po poslednjem očitavanju vodomera</p>
            </div>

            {topMeters.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-400 text-sm">Nema podataka</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-8">#</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Pretplatnik</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Adresa</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stanje (m³)</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Datum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topMeters.map((m, i) => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-400 font-medium">{i + 1}</td>
                        <td className="px-6 py-3 font-medium text-gray-900 max-w-[180px] truncate">{m.subscriber_name}</td>
                        <td className="px-6 py-3 text-gray-500 hidden md:table-cell max-w-[220px] truncate">{m.address}</td>
                        <td className="px-6 py-3 text-right font-bold text-brand-cyan-text">
                          {m.last_reading_value!.toFixed(3)}
                        </td>
                        <td className="px-6 py-3 text-gray-400 text-xs hidden lg:table-cell">
                          {m.last_reading_date
                            ? new Date(m.last_reading_date).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {!hasMeterReading && !hasScada && (
        <div className="text-center text-gray-400 text-sm py-12">Nema dostupnih modula za analitiku.</div>
      )}
    </div>
  )
}
